import type { PresetPassThroughOptions } from '../preset-options'

/** MultiSelect component */
export default {
  multiselect: {
    root: ({ props }: PresetPassThroughOptions) => ({
      class: [
        'relative inline-flex cursor-pointer select-none rounded w-full',
        'bg-transparent border-none appearance-none',
        'ring-1 ring-fp-gray-4',
        'focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-0',
        'disabled:opacity-50 disabled:bg-fp-gray-1 disabled:cursor-not-allowed',
        'transition-colors duration-200',
        {
          'opacity-60 select-none pointer-events-none cursor-default':
            props?.disabled,
        },
      ],
    }),
    labelContainer: {
      class: [
        'overflow-hidden flex-auto flex flex-wrap gap-2 w-full min-h-[3rem] px-3',
      ],
    },
    label: {
      class: [
        'flex items-center gap-1 whitespace-nowrap overflow-hidden text-ellipsis px-3 py-2',
        'text-fp-gray-6 bg-transparent border-none outline-none',
        'disabled:text-fp-gray-5',
      ],
    },
    chipItem: {
      class: [],
    },
    pcChip: {
      root: {
        class: [
          'inline-flex items-center gap-2 px-3 py-1 rounded-sm',
          'bg-primary-0 text-fp-gray-6',
        ],
      },
      removeIcon: {
        class: [
          'cursor-pointer w-4 h-4 rounded-full text-fp-gray-5 hover:text-fp-gray-6',
          'focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-primary',
        ],
      },
    },
    clearIcon: {
      class: [
        'absolute right-10 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5',
        'text-fp-gray-5 hover:text-fp-gray-6 cursor-pointer rounded hover:bg-fp-gray-1 transition-colors z-10',
      ],
    },
    dropdown: {
      class: [
        'flex items-center justify-center shrink-0 bg-transparent text-fp-gray-5 w-10 rounded-e-md',
      ],
    },
    overlay: {
      class: [
        'absolute top-0 left-0 rounded-md bg-white border border-fp-gray-2 shadow-lg max-h-[300px] overflow-auto z-50',
      ],
    },
    header: {
      class: ['flex items-center pt-2 pb-1 px-4 gap-2'],
    },
    pcHeaderCheckbox: {
      root: {
        class: ['relative inline-flex select-none w-5 h-5 align-bottom mr-2'],
      },
      input: {
        class: [
          'peer cursor-pointer appearance-none absolute start-0 top-0 w-full h-full m-0 p-0 opacity-0 z-10 border border-transparent rounded',
        ],
      },
      box: {
        class: [
          'flex justify-center items-center rounded-sm w-5 h-5 border border-fp-gray-3',
          'bg-white text-fp-gray-6',
          'peer-focus-visible:outline-1 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary',
          'transition-colors duration-200',
        ],
      },
      icon: {
        class: ['text-sm w-[0.875rem] h-[0.875rem] transition-none'],
      },
    },
    listContainer: {
      class: ['overflow-auto'],
    },
    list: {
      class: ['py-1', 'm-0 p-1 list-none flex flex-col gap-0.5'],
    },
    optionGroup: {
      class: ['m-0 px-3 py-2 text-fp-gray-5 font-semibold bg-transparent'],
    },
    option: ({ context }: PresetPassThroughOptions) => ({
      class: [
        'px-3 py-2 cursor-pointer transition-colors duration-200 flex items-center gap-2 rounded-sm',
        context?.focused
          ? 'bg-primary-0 text-fp-gray-6'
          : 'text-fp-gray-6 bg-transparent border-none',
      ],
    }),
    optionLabel: {
      class: ['text-fp-gray-6'],
    },
    pcOptionCheckbox: {
      root: {
        class: ['relative inline-flex select-none w-5 h-5 align-bottom mr-2'],
      },
      input: {
        class: [
          'peer cursor-pointer appearance-none absolute start-0 top-0 w-full h-full m-0 p-0 opacity-0 z-10 border border-transparent rounded',
        ],
      },
      box: {
        class: [
          'flex justify-center items-center rounded-sm w-5 h-5 border border-fp-gray-3',
          'bg-white text-fp-gray-6',
          'peer-focus-visible:outline-1 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary',
          'transition-colors duration-200',
        ],
      },
      icon: {
        class: ['text-sm w-[0.875rem] h-[0.875rem] transition-none'],
      },
    },
    emptyMessage: {
      class: ['px-3 py-2 text-fp-gray-5 text-sm'],
    },
    filterIcon: {
      class: ['text-fp-gray-5'],
    },
    pcFilterContainer: {
      root: {
        class: ['relative flex-auto p-2 border-b border-fp-gray-2'],
      },
    },
    pcFilter: {
      root: {
        class: [
          'w-full appearance-none rounded-md outline-none py-2 px-3 pr-10',
          'text-fp-gray-6 bg-white border border-fp-gray-3',
          'focus:ring-2 focus:ring-primary focus:ring-offset-0 focus:border-primary',
          'transition-colors duration-200',
        ],
      },
    },
    pcFilterIconContainer: {
      root: {
        class: [
          'absolute top-1/2 -translate-y-1/2 right-3 z-10 text-fp-gray-5 pointer-events-none',
        ],
      },
    },
    transition: {
      enterFromClass: 'opacity-0 scale-y-95',
      enterActiveClass: 'transition duration-120 ease-out',
      leaveActiveClass: 'transition-opacity duration-100 ease-linear',
      leaveToClass: 'opacity-0',
    },
  },
}
